import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { DrawerOpenState, drawerActions } from '../store/drawerSlice';
import { lightBoxActions } from '../store/lightBoxSlice';
import { missionsActions } from '../store/missionsSlice';
import { StringHelper } from '../helpers/stringHelper';
import MissionMoreInfo from '../components/common/mission/MissionMoreInfo';
import CustomMarkdown from '../components/common/CustomMarkDown';
import Loading from '../components/common/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faHouseChimney } from '@fortawesome/pro-regular-svg-icons';

export enum SideBarState {
  List,
  Details,
  MoreInfo,
}

interface UseMapSidebarStateParams {
  layout: { width: number; height: number };
  theme: any;
  t: (key: string) => string;
  drawerState: DrawerOpenState;
  drawerSuspendCount: number;
  isModalVisible: boolean;
  isLightBoxVisible: boolean;
  isLightBoxBackgroundInteractive: boolean;
  isSecondLightBoxVisible: boolean;
  setResetAirportFlowKey: (key: string) => void;
  setTriggerGetUserLocation: (value: boolean) => void;
  setRefreshMap: (value: boolean) => void;
  setCleanSelection: (value: boolean) => void;
  clearMapSelectionRouteParams: () => void;
  dispatch: any;
}

interface UseMapSidebarStateResult {
  sideBarState: SideBarState;
  setSideBarState: (state: SideBarState) => void;
  branchMarker: string | undefined;
  setBranchMarker: (id: string | undefined) => void;
  preSelectedBranch: { id: string; lat: number; lng: number } | undefined;
  setPreSelectedBranch: (value: { id: string; lat: number; lng: number } | undefined) => void;
  moreInfoTitle: string;
  moreInfoContent: ReactElement;
  missionListFilter: any;
  setMissionListFilter: (filter: any) => void;
  openListRequestId: number;
  setOpenListRequestId: (value: number) => void;
  handleOnBackPress: (overrideSideBarState: boolean) => void;
  handleExploreOtherMissionsFromAirportLimit: () => void;
  handleMoreInfoBack: () => void;
  handleReadMorePress: (info: string) => void;
  handleAdmissibilityPress: (info: string) => void;
  handleConditionsPress: (info: string) => void;
  handleMarkerClickCallBack: (id: string, lat: number, lng: number, source?: 'map-marker' | 'programmatic') => boolean;
}

export const useMapSidebarState = (params: UseMapSidebarStateParams): UseMapSidebarStateResult => {
  const {
    layout,
    theme,
    t,
    drawerState,
    drawerSuspendCount,
    isModalVisible,
    isLightBoxVisible,
    isLightBoxBackgroundInteractive,
    isSecondLightBoxVisible,
    setResetAirportFlowKey,
    setTriggerGetUserLocation,
    setRefreshMap,
    setCleanSelection,
    clearMapSelectionRouteParams,
    dispatch,
  } = params;

  const [sideBarState, setSideBarState] = useState<SideBarState>(SideBarState.List);
  const [branchMarker, setBranchMarker] = useState<string | undefined>(undefined);
  const [preSelectedBranch, setPreSelectedBranch] = useState<{ id: string; lat: number; lng: number } | undefined>(undefined);
  const [missionListFilter, setMissionListFilter] = useState<any>(undefined);
  const [openListRequestId, setOpenListRequestId] = useState(0);
  const [moreInfoTitle, setMoreInfoTitle] = useState('');
  const [moreInfoContent, setMoreInfoContent] = useState<ReactElement>(<></>);
  const drawerSessionRef = useRef(0);

  const handleOnBackPress = useCallback((overrideSideBarState: boolean) => {
    if (overrideSideBarState || sideBarState === SideBarState.Details) {
      if (overrideSideBarState) {
        if (preSelectedBranch) {
          setRefreshMap(true);
        }

        setTriggerGetUserLocation(true);
      }

      setBranchMarker(undefined);

      setPreSelectedBranch(undefined);
      dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));

      setSideBarState(SideBarState.List);
      setCleanSelection(true);
      dispatch(drawerActions.resetState());

      clearMapSelectionRouteParams();
    }
  }, [
    clearMapSelectionRouteParams,
    dispatch,
    preSelectedBranch,
    setCleanSelection,
    setRefreshMap,
    setTriggerGetUserLocation,
    sideBarState,
  ]);

  const handleExploreOtherMissionsFromAirportLimit = useCallback(() => {
    handleOnBackPress(false);
    setResetAirportFlowKey(StringHelper.generateRandomString(8));

    if (Platform.OS !== 'web') {
      dispatch(missionsActions.setShowMissionListView(false));
    }
  }, [dispatch, handleOnBackPress, setResetAirportFlowKey]);

  const handleMoreInfoBack = useCallback(() => {
    setSideBarState(SideBarState.Details);
  }, []);

  const handleReadMorePress = useCallback((info: string) => {
    setMoreInfoTitle('Scenario');
    setMoreInfoContent(
      <MissionMoreInfo title={'Scenario'} onBackPress={handleMoreInfoBack}>
        <CustomMarkdown>{info}</CustomMarkdown>
      </MissionMoreInfo>,
    );
    setSideBarState(SideBarState.MoreInfo);
  }, [handleMoreInfoBack]);

  const handleAdmissibilityPress = useCallback((info: string) => {
    setMoreInfoTitle(t('Admissibility'));
    setMoreInfoContent(
      <MissionMoreInfo title={t('Admissibility')} onBackPress={handleMoreInfoBack}>
        <CustomMarkdown>{info}</CustomMarkdown>
      </MissionMoreInfo>,
    );
    setSideBarState(SideBarState.MoreInfo);
  }, [handleMoreInfoBack, t]);

  const handleConditionsPress = useCallback((info: string) => {
    setMoreInfoTitle(t('Terms-and-Conditions'));
    setMoreInfoContent(
      <MissionMoreInfo title={t('Terms-and-Conditions')} onBackPress={handleMoreInfoBack}>
        <CustomMarkdown>{info}</CustomMarkdown>
      </MissionMoreInfo>,
    );
    setSideBarState(SideBarState.MoreInfo);
  }, [handleMoreInfoBack, t]);

  const handleMarkerClickCallBack = useCallback((id: string, lat: number, lng: number, source: 'map-marker' | 'programmatic' = 'programmatic') => {
    const drawerSessionId = drawerSessionRef.current + 1;
    drawerSessionRef.current = drawerSessionId;

    if (layout.width <= 1200) {
      const hasBlockingPrimaryLightBox = isLightBoxVisible && !isLightBoxBackgroundInteractive;
      const hasBlockingOverlay = isModalVisible || hasBlockingPrimaryLightBox || isSecondLightBoxVisible;

      if (source === 'map-marker' && hasBlockingOverlay) {
        return false;
      }

      // Clear stale suspend state when no overlay is active.
      if (!hasBlockingOverlay && drawerSuspendCount > 0) {
        dispatch(drawerActions.clearSuspendCount());
      }

      // Targeted stale second-layer cleanup for mobile drawer re-open.
      dispatch(lightBoxActions.setOnSecondClose(undefined));
      dispatch(lightBoxActions.setSecondContent(<></>));
      dispatch(lightBoxActions.setSecondVisibility(false));
    }

    if (layout.width <= 1200) {
      const loadingContent = (
        <View style={{ height: 150, padding: 16, alignItems: 'center', justifyContent: 'center' }}>
          <Loading />
        </View>
      );

      if (drawerState === DrawerOpenState.opened) {
        dispatch(drawerActions.setContent(loadingContent));
      } else {
        dispatch(
          drawerActions.setConfig({
            showButtonBar: false,
            title: `${t('Missions')}`,
            buttonBarConfig: undefined,
            showCloseButton: true,
            showIcon: true,
            icon: <FontAwesomeIcon icon={faHouseChimney} size={24} color={theme.text.primary.toString()} />,
            state: DrawerOpenState.opened,
            resizable: true,
            content: loadingContent,
            showTitle: true,
            suspendCount: drawerSuspendCount,
            onClose: () => {
              setTimeout(() => {
                if (drawerSessionRef.current !== drawerSessionId) {
                  return;
                }

                setBranchMarker(undefined);
                setPreSelectedBranch(undefined);
                dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
                clearMapSelectionRouteParams();
              }, 0);
            },
          }),
        );
      }
    }
    setBranchMarker(id);
    dispatch(missionsActions.setSelectedBranch({ id: id, lat: lat, lng: lng }));
    setSideBarState(SideBarState.Details);
    return true;
  }, [
    dispatch,
    drawerState,
    drawerSuspendCount,
    isLightBoxBackgroundInteractive,
    isLightBoxVisible,
    isModalVisible,
    isSecondLightBoxVisible,
    layout.width,
    t,
    theme.text.primary,
    clearMapSelectionRouteParams,
  ]);

  return {
    sideBarState,
    setSideBarState,
    branchMarker,
    setBranchMarker,
    preSelectedBranch,
    setPreSelectedBranch,
    moreInfoTitle,
    moreInfoContent,
    missionListFilter,
    setMissionListFilter,
    openListRequestId,
    setOpenListRequestId,
    handleOnBackPress,
    handleExploreOtherMissionsFromAirportLimit,
    handleMoreInfoBack,
    handleReadMorePress,
    handleAdmissibilityPress,
    handleConditionsPress,
    handleMarkerClickCallBack,
  };
};

export default useMapSidebarState;
